'use client';

import { motion } from 'framer-motion';
import {
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { cn } from '@/lib/ui-kit/utils';
import type { HighlightBox } from './hooks/useResultNavigation';

/** One height, whatever the list holds: the panel never jumps as answers come and go. */
const LIST_HEIGHT = 'h-[340px]';
const EDGE_THRESHOLD_PX = 2;

/** A light sweeping along the hairline while a newer query loads. */
const SWEEP_TRANSITION = { duration: 0.7, ease: [0.2, 0.8, 0.2, 1], repeat: Infinity } as const;

type Edge = 'top' | 'bottom';

const EDGE_CLASS: Record<Edge, string> = {
  top: 'top-0 bg-linear-to-b',
  bottom: 'bottom-0 bg-linear-to-t',
};

interface EdgeFadeProps {
  edge: Edge;
  visible: boolean;
}

/** Shade over a cut edge of the list: the top once scrolled, the bottom while more is below. */
function EdgeFade({ edge, visible }: EdgeFadeProps) {
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-x-0 h-7 from-v2-surface-secondary to-transparent transition-opacity duration-200',
        EDGE_CLASS[edge],
        visible ? 'opacity-100' : 'opacity-0',
      )}
    />
  );
}

interface ProgressLineProps {
  active: boolean;
}

/** The hairline under the tabs, carrying the search's progress. */
function ProgressLine({ active }: ProgressLineProps) {
  return (
    <div className="relative h-px overflow-hidden bg-v2-border dark:bg-v2-surface-tertiary">
      {active ? (
        <motion.span
          aria-hidden
          className="absolute inset-y-0 left-0 w-2/5 bg-linear-to-r from-transparent via-v2-bg-blue to-transparent"
          initial={{ x: '-100%' }}
          animate={{ x: '260%' }}
          transition={SWEEP_TRANSITION}
        />
      ) : null}
    </div>
  );
}

interface ResultsFrameProps {
  listRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  listId: string;
  highlight: HighlightBox | null;
  refreshing: boolean;
  onPointerMove: (e: PointerEvent<HTMLElement>) => void;
  children: ReactNode;
}

/**
 * The header panel's list: a hairline with the search's progress, the rows
 * scrolling under one sliding highlight, and fades where the scroll cuts them.
 */
export function ResultsFrame({
  listRef,
  contentRef,
  listId,
  highlight,
  refreshing,
  onPointerMove,
  children,
}: ResultsFrameProps) {
  const [edges, setEdges] = useState({ top: false, bottom: false });

  const updateEdges = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const top = list.scrollTop > EDGE_THRESHOLD_PX;
    const bottom = list.scrollHeight - list.scrollTop - list.clientHeight > EDGE_THRESHOLD_PX;
    setEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, [listRef]);

  // Content grows and shrinks without a scroll: answers landing, the recent block folding.
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const observer = new ResizeObserver(updateEdges);
    observer.observe(content);
    return () => observer.disconnect();
  }, [contentRef, updateEdges]);

  const highlightStyle: CSSProperties = {
    height: highlight?.height,
    transform: `translateY(${highlight?.top ?? 0}px)`,
  };

  return (
    <>
      <ProgressLine active={refreshing} />
      <div className="relative">
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          onScroll={updateEdges}
          onPointerMove={onPointerMove}
          className={cn(
            'scrollbar-thumb-thin overflow-y-auto overscroll-contain p-1.5',
            LIST_HEIGHT,
          )}
        >
          {/* At least the list's height, so an empty state can sit in its middle. */}
          <div ref={contentRef} className="relative isolate flex min-h-full flex-col">
            <div
              aria-hidden
              style={highlightStyle}
              className={cn(
                'pointer-events-none absolute inset-x-0 top-0 -z-10 rounded-[8px] bg-v2-neutral-surface transition-[transform,height,opacity] duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)] dark:bg-v2-surface-tertiary',
                !highlight && 'opacity-0',
              )}
            />
            {children}
          </div>
        </div>
        <EdgeFade edge="top" visible={edges.top} />
        <EdgeFade edge="bottom" visible={edges.bottom} />
      </div>
    </>
  );
}
